import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton, MultiLanguageTextInput } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

export const EditFaqCategory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("faqCategory");
  const isRTL = i18n.language === "ar";

  const [formData, setFormData] = useState({
    id: Number(id),
    name: "",
    description: "",
    displayOrder: 0,
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchFaqCategory = async () => {
      try {
        const response = await adminApi.call(`/faqcategory/${id}`, "GET");

        if (!response.success) {
          showErrorToast(response.error || t("messages.fetchError"));
          return;
        }

        setFormData({
          id: response.data.id,
          name: response.data.name || "",
          description: response.data.description || "",
          displayOrder: response.data.displayOrder || 0,
          isActive: response.data.isActive ?? true
        });
      } catch (error: any) {
        showErrorToast(t("messages.fetchError") + ": " + error.message);
      }
    };

    fetchFaqCategory();
  }, [id]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = t("validation.nameRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitUpdate = async () => {
    if (!validateForm()) {
      showErrorToast(t("validation.fillAllFields"));
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        description: formData.description.trim() ? formData.description : null
      };

      const response = await adminApi.call("/faqcategory/admin/Update", "PUT", payload);

      if (!response.success) {
        showErrorToast(response.error || t("messages.updateError"));
        return;
      }

      showSuccessToast(t("messages.updateSuccess"));
    } catch (error: any) {
      showErrorToast(t("messages.updateError") + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title={t("editFaqCategory")}
      subtitle={t("subtitle")}
      breadcrumbs={[
        { label: t("breadcrumbs.faqCategories"), path: "/faq-categories" },
        { label: t("breadcrumbs.edit") }
      ]}
      className={isRTL ? "rtl" : "ltr"}
    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        <div className="row g-3">
          <div className="col-12">
            <MultiLanguageTextInput
              label={t("fields.name")}
              value={formData.name}
              onChange={(value) => {
                setFormData((prev) => ({ ...prev, name: value }));
                if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
              }}
              placeholder={t("placeholders.name")}
              error={errors.name}
              disabled={loading}
              required
              isRTL={isRTL}
            />
          </div>

          <div className="col-12">
            <MultiLanguageTextInput
              label={t("fields.description")}
              value={formData.description}
              onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
              placeholder={t("placeholders.description")}
              disabled={loading}
              isRTL={isRTL}
            />
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? "text-end" : "text-start"}`}>{t("fields.displayOrder")}</label>
            <input
              type="number"
              className={`form-control ${isRTL ? "text-end" : "text-start"}`}
              value={formData.displayOrder}
              onChange={(e) => setFormData((prev) => ({ ...prev, displayOrder: Number(e.target.value) || 0 }))}
            />
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? "text-end" : "text-start"}`}>{t("fields.isActive")}</label>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                checked={formData.isActive}
                onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              <label className={`form-check-label ${isRTL ? "text-end" : "text-start"}`}>
                {formData.isActive ? t("status.active") : t("status.inactive")}
              </label>
            </div>
          </div>
        </div>

        <div className="text-center mt-4">
          <ActionButton variant="secondary" onClick={() => navigate("/faq-categories")} className={isRTL ? "ms-2" : "me-2"}>
            <i className={`fa ${isRTL ? "fa-arrow-right" : "fa-arrow-left"} ${isRTL ? "ms-1" : "me-1"}`}></i>
            {t("actions.back")}
          </ActionButton>
          <ActionButton variant="primary" onClick={submitUpdate} loading={loading} disabled={loading}>
            {loading ? t("actions.updating") : t("actions.update")}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
